<template>
  <layout-report-step
    :md="md"
    :isDone="isDone"
    :title="header.title"
    :description="header.description"
    :debug="[numberplate, user, contact, address]"
  >
    <v-card flat max-width="500" class="pt-4 pl-4 pr-4">
      <v-form ref="form" v-model="valid">
        <v-subheader>{{ $t("report.formPersonalData.numberplateForm") }}</v-subheader>
        <numberplate-form
          v-model="numberplate"
          :valid="valid"
          v-on:valid="setValid"
          :countryCode="countryCode"
        ></numberplate-form>

        <v-subheader>{{ $t("report.formPersonalData.subtitle") }}</v-subheader>
        <v-text-field
          :value="user.firstName"
          @blur="updateUserFirstName"
          :label="$t('report.formPersonalData.firstName')"
          :rules="noNumberRule"
          required
          data-test-form-firstName
          outlined
        ></v-text-field>
        <v-text-field
          :value="user.lastName"
          @blur="updateUserLastName"
          :label="$t('report.formPersonalData.lastName')"
          :rules="noNumberRule"
          required
          data-test-form-lastName
          outlined
        >
        </v-text-field>

        <v-text-field
          :label="$t('report.formPersonalData.mail')"
          :value="contact.email"
          @blur="updateUserEmail"
          type="email"
          :rules="emailRules"
          autocomplete="email"
          required
          data-test-form-email
          outlined
        ></v-text-field>

        <v-text-field
          :label="$t('report.formPersonalData.phone')"
          :value="contact.phone"
          @blur="updateUserPhone"
          type="tel"
          :rules="phoneNumberRules"
          required
          data-test-form-phone
          outlined
        ></v-text-field>
        <v-checkbox
          v-if="isWhatsApp"
          v-model="whatsAppContactOptin"
          :label="$t('report.formPersonalData.whatsAppContactOptin')"
          data-test-whatsapp-checkbox
        />
        <v-text-field
          :label="$t('report.formPersonalData.street')"
          :value="address.street"
          @blur="updateUserStreet"
          :rules="[(v) => !!v || this.$t('report.formPersonalData.mandatoryInput')]"
          required
          data-test-form-street
          outlined
        ></v-text-field>
        <v-text-field
          :label="$t('report.formPersonalData.zip')"
          type="number"
          :value="address.zip"
          @blur="updateUserZip"
          :rules="zipRules"
          required
          data-test-form-zip
          outlined
        ></v-text-field>
        <v-text-field
          :label="$t('report.formPersonalData.city')"
          :value="address.city"
          @blur="updateUserCity"
          :rules="noNumberRule"
          required
          data-test-form-city
          outlined
        ></v-text-field>
      </v-form>
    </v-card>
  </layout-report-step>
</template>

<script lang="ts">
import LayoutReportStep from "@/layouts/LayoutReportStep.vue";
import NumberplateForm from "@/components/utility/NumberplateForm.vue";

import { ReportModule } from "@/store/modules/report.store";
import { Component } from "vue-property-decorator";

import { IReportHeaderData } from "@/lib/interfaces/Report/IReportHeaderData";
import { IReportStep } from "@/lib/interfaces/Report/IViewReportStep";
import { emailRule, germanZipRule, noNumberRule, phoneRule } from "@/lib/rules/contactRule.ts";
import { numberplateRule } from "@/lib/rules/numberplateRule.ts";
import { requiredRule } from "@/lib/rules/requiredRule.ts";
import ReportStepMixin from "@/mixins/ReportStepMixin.vue";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";
import { LoginModule } from "@/store/modules/login.store";
import { CountryCodeEnum } from "@/lib/enum/country-code.enum";
import { parsePhone } from "@/lib/utility/parsePhone";

@Component({
  components: {
    LayoutReportStep,
    NumberplateForm
  }
})
export default class ReportPersonalData extends ReportStepMixin implements IReportStep {
  public md = 4;
  public static screenName = ReportScreenEnum.contactdetails;
  public header: IReportHeaderData = {
    title: "report.formPersonalData.title",
    description: "report.formPersonalData.description"
  };
  screen = ReportScreenEnum.contactdetails;

  phone: any;

  numberplateValid = false;

  valid = false;

  setValid(value: boolean) {
    this.numberplateValid = value;
  }

  get isDone() {
    return this.valid && this.numberplateValid;
  }

  get numberPlateRules() {
    const rules = [];

    rules.push(requiredRule());
    rules.push(numberplateRule());

    return rules;
  }

  get noNumberRule() {
    const rules = [];

    rules.push(requiredRule());
    rules.push(noNumberRule());

    return rules;
  }

  get emailRules() {
    const rules = [];

    rules.push(requiredRule());
    rules.push(emailRule());

    return rules;
  }

  get phoneNumberRules() {
    const rules = [];

    rules.push(requiredRule());
    rules.push(phoneRule());

    return rules;
  }

  get zipRules() {
    const rules = [];

    rules.push(requiredRule());
    rules.push(germanZipRule());

    return rules;
  }

  get whatsAppContactOptin(): boolean | undefined {
    if (!this.isWhatsApp) {
      return undefined;
    }

    return ReportModule.whatsAppContactOptIn || false;
  }

  set whatsAppContactOptin(value: boolean | undefined) {
    ReportModule.setWhatsAppContactOptIn(value);
  }

  get numberplate(): string {
    return ReportModule.numberplate;
  }

  set numberplate(value: string) {
    ReportModule.setNumberplate(value);
  }

  get user(): any {
    return ReportModule.user;
  }

  get contact(): any {
    return ReportModule.contact;
  }

  get address(): any {
    return ReportModule.address;
  }

  get countryCode(): CountryCodeEnum {
    return ReportModule.partnerCountryCode;
  }

  get partner() {
    return ReportModule.partner;
  }

  get isWhatsApp() {
    return this.partner?.settings?.isWhatsApp || false;
  }

  updateNumberplate(e: any) {
    ReportModule.setNumberplate(e.target.value.toUpperCase());
  }

  updateWhatsAppContactOptIn(e: boolean) {
    ReportModule.setWhatsAppContactOptIn(e);
  }

  updateUserFirstName(e: any) {
    ReportModule.setUser({ firstName: e.target.value });
  }

  updateUserLastName(e: any) {
    ReportModule.setUser({ lastName: e.target.value });
  }

  updateUserPhone(e: any) {
    ReportModule.setContact({ phone: parsePhone(e.target.value, ReportModule.partner.countryCode as CountryCodeEnum) });
  }

  updateUserEmail(e: any) {
    ReportModule.setContact({ email: e.target.value });
    LoginModule.setCurrentMail(e.target.value);
  }

  updateUserCity(e: any) {
    ReportModule.setAddress({ city: e.target.value });
  }

  updateUserState(e: any) {
    ReportModule.setAddress({ state: e.target.value });
  }

  updateUserStreet(e: any) {
    ReportModule.setAddress({ street: e.target.value });
  }

  updateUserZip(e: any) {
    ReportModule.setAddress({ zip: e.target.value });
  }
}
</script>

<style></style>
